import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, LogOut, Package, ShieldCheck, KeyRound, Sparkles, Mail } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import LogoChip from "@/components/LogoChip";
import ChangePasswordModal from "@/components/ChangePasswordModal";

const SECTIONS = ["home", "products", "story", "active", "support"];

export default function Nav() {
  const {
    t, lang, toggleLang, cartCount, setCartOpen, setMenuOpen, toast, scrollToSection,
    user, authLoading, openAuth, logout, setFaqOpen,
  } = useStore();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [accountOpen, setAccountOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    SECTIONS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const links = [
    { id: "home", key: "nav.home", testid: "nav-home-link" },
    { id: "products", key: "nav.shop", testid: "nav-shop-link" },
    { id: "story", key: "nav.story", testid: "nav-story-link" },
    { id: "active", key: "nav.active", testid: "nav-active-link" },
    { id: "support", key: "nav.support", testid: "nav-support-link" },
  ];

  return (
    <header className={`nav-wrap ${scrolled ? "scrolled" : ""}`}>
      <nav className="nav">
        <a
          href="#home"
          className="logo"
          data-testid="logo-link"
          onClick={(e) => { e.preventDefault(); navigate("/"); scrollToSection("home"); }}
        >
          <LogoChip />
        </a>

        <div className="nav-center">
          {links.map((l) => (
            <button
              key={l.id}
              className={`nav-link ${active === l.id ? "active" : ""}`}
              data-testid={l.testid}
              onClick={() => scrollToSection(l.id)}
            >
              {t(l.key)}
            </button>
          ))}
        </div>

        <div className="nav-actions">
          <button
            className="lang-btn"
            data-testid="language-toggle-button"
            onClick={() => { toggleLang(); toast(t("toast.language")); }}
            aria-label="Switch language"
          >
            <span className={lang === "kh" ? "on" : "off"}>ខ្មែរ</span>
            <span className="sep">|</span>
            <span className={lang === "en" ? "on" : "off"}>EN</span>
          </button>

          <button
            className="icon-btn account-btn"
            data-testid="account-button"
            aria-label="Account"
            onClick={() => (user ? setAccountOpen((o) => !o) : openAuth("login"))}
          >
            <User size={18} />
          </button>

          <button
            className="icon-btn"
            data-testid="cart-drawer-trigger"
            aria-label="Shopping bag"
            onClick={() => setCartOpen(true)}
          >
            <ShoppingBag size={18} />
            {cartCount > 0 && <span className="cart-count" data-testid="cart-count-badge">{cartCount}</span>}
          </button>

          <button className="icon-btn mobile-menu-btn" data-testid="mobile-menu-button" aria-label="Menu" onClick={() => setMenuOpen(true)}>
            <Menu size={18} />
          </button>
        </div>

        {accountOpen && user && (
          <>
            <div className="menu-backdrop" onClick={() => setAccountOpen(false)} data-testid="account-menu-backdrop" />
            <div className="account-menu" data-testid="account-menu">
              <div className="account-menu-head">
                {user.membership_status === "active" && (
                  <span className="member-badge" data-testid="account-member-badge">✦ {t("auth.memberBadge")}</span>
                )}
                <strong>{user.name}</strong>
                <small>{user.email}</small>
                {!user.email_verified && (
                  <span className="verify-pill" data-testid="account-email-unverified">{t("auth.notVerified")}</span>
                )}
                <span className="points-pill" data-testid="account-points">{user.points} {t("auth.points")}</span>
              </div>
              <button data-testid="account-my-orders" onClick={() => { setAccountOpen(false); navigate("/my-orders"); }}>
                <Package size={15} /> {t("auth.myOrders")}
              </button>
              <button data-testid="account-membership" onClick={() => { setAccountOpen(false); navigate("/membership"); }}>
                <Sparkles size={15} /> {t("auth.membership")}
              </button>
              {!user.email_verified && (
                <button data-testid="account-verify-email" onClick={() => { setAccountOpen(false); openAuth("verify"); }}>
                  <Mail size={15} /> {t("auth.verifyNow")}
                </button>
              )}
              <button data-testid="account-change-password" onClick={() => { setAccountOpen(false); setPwOpen(true); }}>
                <KeyRound size={15} /> {t("auth.changePassword")}
              </button>
              <button data-testid="account-track" onClick={() => { setAccountOpen(false); navigate("/track"); }}>
                <Search size={15} /> {t("footer.track")}
              </button>
              {user.role === "admin" && (
                <button data-testid="account-owner-panel" onClick={() => { setAccountOpen(false); navigate("/owner"); }}>
                  <ShieldCheck size={15} /> {t("auth.ownerPanel")}
                </button>
              )}
              <button data-testid="account-logout" onClick={() => { setAccountOpen(false); logout(); }}>
                <LogOut size={15} /> {t("auth.logout")}
              </button>
            </div>
          </>
        )}

        <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      </nav>
    </header>
  );
}
